import { FC, useState, useEffect, SetStateAction, Dispatch } from 'react';
import { Field, useFormik, FormikProvider } from 'formik';
import * as Yup from 'yup';
import { TextField, Select, Button, Text, StatusIconProtoProps, Card } from '@nike/eds';
import { useUserContext } from '../../../../context/user';
import { GuestPass, PassRequest } from '../../../../models/IPassRequest';
import { StoreOption } from '../type';
import ts from '../i18n';
import { useI18nContext } from '../../../../context/language/use-i18n-context';
import { getMinDate, getUserShortDate } from '../../../../utils/date-helper';
import { AppConfig } from '../../../../utils/constants';
import { CompPassRequest } from '../../../../models/ICompPass';
import { CompNumberCheck } from '../../../../services/comp-service';
import { registerPass } from '../../../../services/pass-service';
import { HttpStatusCode } from 'axios';
import '../comp-pass.css';
import { Approver } from '.././type';

export interface Props {
  compPassToEdit: CompPassRequest;
  stores: StoreOption[];
  onDismiss: () => void;
  setSnackBar: Dispatch<
    SetStateAction<{
      showSnack: boolean;
      message: string;
      snackStatus: StatusIconProtoProps['status'];
    }>
  >;
  approver: Approver | undefined;
}

export const CompForm: FC<Props> = ({
  compPassToEdit,
  stores,
  onDismiss,
  setSnackBar,
  approver,
}) => {
  const { i18nString } = useI18nContext();
  const userInfo = useUserContext();
  const [saving, setSaving] = useState<boolean>(false);
  const [compAmountMax] = useState<number>(() => {
    switch (userInfo?.passplayUser?.userCountry) {
      case 'MX':
        return 6;
      case 'JP':
        return 8;
      case 'KR':
        return 9;
      default:
        return 4;
    }
  });
  const [compNumberValid, setCompNumberValid] = useState<boolean>(false);

  const compPassDefault: GuestPass = {
    passId: compPassToEdit.passId,
    sponsorId: compPassToEdit.sponsorId,
    passTypeId: compPassToEdit.passTypeId,
    passPolicyId: compPassToEdit.passPolicyId,
    companyName: compPassToEdit.companyName ?? '',
    firstName: compPassToEdit.firstName ?? '',
    lastName: compPassToEdit.lastName ?? '',
    passLocationId: compPassToEdit.passLocationId ?? 0,
    shoppingDateStart: compPassToEdit.shoppingDateStart
      ? new Date(compPassToEdit.shoppingDateStart)
      : new Date(),
    shoppingDateEnd: compPassToEdit.shoppingDateEnd
      ? new Date(compPassToEdit.shoppingDateEnd)
      : new Date(),
    compAmount: compPassToEdit.compAmount ?? '',
    compNumber: compPassToEdit.compNumber ?? null,
    emailAddress: '',
    relationshipId: null,
    weChatId: null,
    timeSlotId: null,
    govtComplianceChecked: false,
    emailLanguageGroupId: null,
    guestEmailLanguage: '',
  };

  const validationSchema = Yup.object().shape({
    approver: Yup.string().required(),
    firstName: Yup.string()
      .required(i18nString(ts.VALIDATION_REQUIRED_FIELD))
      .matches(
        RegExp(AppConfig.regexPatterns.regexPersonNames),
        AppConfig.validationMessages.namesErrorMessage,
      ),
    lastName: Yup.string()
      .required(i18nString(ts.VALIDATION_REQUIRED_FIELD))
      .matches(
        RegExp(AppConfig.regexPatterns.regexPersonNames),
        AppConfig.validationMessages.namesErrorMessage,
      ),
    companyName: Yup.string()
      .required(i18nString(ts.VALIDATION_REQUIRED_FIELD))
      .matches(
        RegExp(AppConfig.regexPatterns.regexPersonNames),
        AppConfig.validationMessages.namesErrorMessage,
      ),
    compAmount: Yup.number()
      .typeError('Comp Amount must be a number')
      .required(i18nString(ts.VALIDATION_REQUIRED_FIELD)),
    compNumber: Yup.number()
      .typeError('Comp Number must be a number')
      .required(i18nString(ts.VALIDATION_REQUIRED_FIELD))
      .test('compNumber-check', i18nString(ts.COMP_INVALID_MESSAGE), async function (value) {
        if (!value) return false;
        try {
          const response = await CompNumberCheck(Number(value));
          setCompNumberValid(response.data);
          return response.data === true;
        } catch (error) {
          return this.createError({ message: 'Error validating Comp Number' });
        }
      }),
    passLocationId: Yup.number().required(),
    shoppingDateStart: Yup.string().required(),
  });

  const formik = useFormik({
    validateOnMount: true,
    initialValues: compPassToEdit,
    enableReinitialize: true,
    onSubmit: () => {
      formik.resetForm();
    },
    validationSchema: validationSchema,
  });

  useEffect(() => {
    if (approver) {
      formik.setFieldValue('approver', approver.id);
    }
  }, [approver]);

  const handleSubmit = async () => {
    setSaving(true);

    let passId = null;
    if (!!compPassToEdit.passId && Number(compPassToEdit.passId) > 0)
      passId = compPassToEdit.passId;

    const compPass: PassRequest = {
      approver: approver?.id ?? '',
      guestPasses: [
        {
          ...compPassDefault,
          passId: passId,
          companyName: formik.values.companyName,
          firstName: formik.values.firstName,
          lastName: formik.values.lastName,
          passLocationId: formik.values.passLocationId,
          shoppingDateStart: new Date(formik.values.shoppingDateStart),
          shoppingDateEnd: new Date(
            new Date(formik.values.shoppingDateStart).setDate(
              new Date(formik.values.shoppingDateStart).getDate() + compPassToEdit.shoppingDays - 1,
            ),
          ),
          compAmount: formik.values.compAmount,
          compNumber: formik.values.compNumber,
        } as GuestPass,
      ],
    };

    try {
      const response = await registerPass(userInfo.passplayUser?.sponsorId, compPass);
      if (response.status === HttpStatusCode.Ok) {
        if (Number(compPassToEdit.passId) > 0) {
          setSnackBar({
            showSnack: true,
            message: i18nString(ts.TOASTR_CHANGES_SAVED).replace(/\{{\w+\}}/g, 'COMP'),
            snackStatus: 'success' as StatusIconProtoProps['status'],
          });
        } else {
          setSnackBar({
            showSnack: true,
            message: i18nString(ts.TOASTR_PASS_ADDED).replace(/\{{\w+\}}/g, 'COMP'),
            snackStatus: 'success' as StatusIconProtoProps['status'],
          });
        }
      } else {
        setSnackBar({
          showSnack: true,
          message: i18nString(ts.TOASTR_PASS_ERROR),
          snackStatus: 'error' as StatusIconProtoProps['status'],
        });
      }
      onDismiss();
    } catch (e) {
      setSaving(false);
      setSnackBar({
        showSnack: true,
        message: i18nString(ts.TOASTR_PASS_ERROR),
        snackStatus: 'error' as StatusIconProtoProps['status'],
      });
      onDismiss();
    }
    setSaving(false);

    formik.resetForm();
  };

  function formValidate(compPass: CompPassRequest) {
    if (
      compPass.approver.length <= 0 ||
      compPass.approver === null ||
      compPass.approver === '' ||
      compNumberValid === false
    ) {
      return false;
    }
    return true;
  }

  return (
    <section>
      <FormikProvider value={formik}>
        <Card className="section-border pass_modal_width">
          <div className="comp__form-card eds-flex eds-flex--direction-row eds-flex--wrap eds-flex--align-content-flex-start eds-flex--justify-content-space-between">
            <div className="column">
              <Text className="h4_font eds-spacing--mt-12 eds-spacing--mt-4" as={'h4'}>
                {i18nString(ts.PASS_MODAL_GUEST_INFO_HEADING)}
              </Text>
              <TextField
                id="companyName"
                label={i18nString(ts.PLACEHOLDER_COMPANY_NAME)}
                aria-label={i18nString(ts.PLACEHOLDER_COMPANY_NAME)}
                hideLabel={true}
                placeholder={i18nString(ts.PLACEHOLDER_COMPANY_NAME)}
                type="text"
                name="companyName"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="comp__form-card eds-spacing--mt-16"
                value={formik.values.companyName}
                data-testid="company_name"
                hasErrors={formik.touched.companyName && !!formik.errors.companyName}
                errorMessage={formik.touched.companyName ? formik.errors.companyName : ''}
              />
              <TextField
                id="firstName"
                label={i18nString(ts.PLACEHOLDER_FIRST_NAME)}
                aria-label={i18nString(ts.PLACEHOLDER_FIRST_NAME)}
                hideLabel={true}
                placeholder={i18nString(ts.PLACEHOLDER_FIRST_NAME)}
                type="text"
                name="firstName"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="comp__form-card eds-spacing--mt-16"
                value={formik.values.firstName}
                data-testid="first_name"
                hasErrors={formik.touched.firstName && !!formik.errors.firstName}
                errorMessage={formik.touched.firstName ? formik.errors.firstName : ''}
              />
              <TextField
                id="lastName"
                label={i18nString(ts.PLACEHOLDER_LAST_NAME)}
                aria-label={i18nString(ts.PLACEHOLDER_LAST_NAME)}
                hideLabel={true}
                placeholder={i18nString(ts.PLACEHOLDER_LAST_NAME)}
                type="text"
                name="lastName"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="comp__form-card eds-spacing--mt-16"
                value={formik.values.lastName}
                data-testid="last_name"
                hasErrors={formik.touched.lastName && !!formik.errors.lastName}
                errorMessage={formik.touched.lastName ? formik.errors.lastName : ''}
              />
              <Text className="h4_font eds-spacing--mt-12 eds-spacing--mx-4" as={'h4'}>
                {i18nString(ts.PASS_MODAL_STORE_HEADING)}
              </Text>
              <Field name="passLocationId">
                {({ field }: { field: any }) => (
                  <Select
                    {...field}
                    id="passLocationId"
                    aria-label="store"
                    options={stores}
                    label="store"
                    name="passLocationId"
                    className="comp__form-card eds-spacing--mt-16"
                    value={
                      stores.find(option => option.value === formik.values.passLocationId) || ''
                    }
                    onChange={(option: any) => {
                      formik.setFieldValue('passLocationId', option.value);
                    }}
                    onBlur={formik.handleBlur}
                    hideLabel={true}
                    isSearchable={false}
                    hasErrors={formik.touched.passLocationId && !!formik.errors.passLocationId}
                    errorMessage={i18nString(ts.VALIDATION_REQUIRED_FIELD)}
                  />
                )}
              </Field>
            </div>
            <div className="column">
              <Text className="h4_font eds-spacing--mt-12 eds-spacing--mx-4" as={'h4'}>
                {i18nString(ts.PASS_MODAL_DATE_HEADING)}
              </Text>
              <TextField
                label="shoppingDateStart"
                hideLabel={true}
                aria-label="shoppingDateStart"
                data-testid="shoppingDateStart"
                type="date"
                name="shoppingDateStart"
                value={formik.values.shoppingDateStart}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="comp__form-card eds-spacing--mt-16"
                min={getMinDate()}
                id="shoppingDateStart"
                placeholder={getUserShortDate(userInfo?.passplayUser?.userCountry || '')}
                hasErrors={formik.touched.shoppingDateStart && !!formik.errors.shoppingDateStart}
                errorMessage={i18nString(ts.VALIDATION_REQUIRED_FIELD)}
              />

              <Text className="h4_font eds-spacing--mt-12 eds-spacing--mx-4" as={'h4'}>
                {i18nString(ts.PASS_MODAL_COMP_AMOUNT_HEADING)}
              </Text>
              <TextField
                id="compAmount"
                label={i18nString(ts.PASS_MODAL_COMP_AMOUNT_HEADING)}
                aria-label={i18nString(ts.PASS_MODAL_COMP_AMOUNT_HEADING)}
                hideLabel={true}
                placeholder={i18nString(ts.PLACEHOLDER_DOLLAR_SIGN)}
                type="text"
                name="compAmount"
                onChange={e => {
                  let value = e.target.value;
                  if (isNaN(Number(value)) || Number(value) < 0) {
                    value = '';
                  } else {
                    const maxLen = compAmountMax;
                    if (value.length > maxLen) {
                      value = value.slice(0, maxLen);
                    }
                  }
                  formik.setFieldValue('compAmount', value);
                }}
                onBlur={formik.handleBlur}
                className="comp__form-card eds-spacing--mt-16"
                value={formik.values.compAmount}
                data-testid="comp_amount"
                message={
                  Number(formik.values.compAmount) > 1000
                    ? i18nString(ts.PASS_MODAL_COMP_AMOUNT_HELP_TEXT)
                    : ''
                }
                hasErrors={!!formik.errors.compAmount && formik.touched.compAmount}
                errorMessage={formik.touched.compAmount ? formik.errors.compAmount : ''}
              />

              <Text className="h4_font eds-spacing--mt-12 eds-spacing--mx-4" as={'h4'}>
                {i18nString(ts.PASS_MODAL_COMP_NUMBER_HEADING)}
              </Text>
              <TextField
                id="compNumber"
                label={i18nString(ts.PLACEHOLDER_COMP_NUMBER)}
                aria-label={i18nString(ts.PLACEHOLDER_COMP_NUMBER)}
                hideLabel={true}
                placeholder={i18nString(ts.PLACEHOLDER_COMP_NUMBER)}
                type="text"
                name="compNumber"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="comp__form-card eds-spacing--mt-16"
                value={formik.values.compNumber}
                data-testid="comp_number"
                hasErrors={!!formik.errors.compNumber && formik.touched.compNumber}
                errorMessage={formik.touched.compNumber ? formik.errors.compNumber : ''}
              />
            </div>
          </div>
        </Card>
      </FormikProvider>
      <div className="eds-flex eds-flex--justify-content-center eds-spacing--m-48">
        {!saving && (
          <Button
            type="submit"
            disabled={!formik.isValid || !formValidate(formik.values)}
            className="txt_uppercase"
            aria-label="saveCompPassButton"
            variant="primary"
            data-testid="register"
            onClick={handleSubmit}
          >
            {compPassToEdit?.passId && compPassToEdit.passId > 0
              ? i18nString(ts.PASS_MODAL_UPDATE)
              : i18nString(ts.PASS_MODAL_REGISTER)}
          </Button>
        )}
        {saving && <span>{i18nString(ts.SAVING_TEXT)}</span>}
      </div>
    </section>
  );
};
